import { useMemo, useState } from "react";

import { downloadText, generateKeyAndCsr } from "../lib/csr";

/** Self-service enrollment: the recipient opens their one-time link, their
 * browser generates the key, and only the CSR is sent (with the token). */
export function EnrollPage() {
  const token = useMemo(() => {
    const query = window.location.hash.split("?")[1] ?? "";
    return new URLSearchParams(query).get("token") ?? "";
  }, []);

  const [cn, setCn] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    subject: string;
    not_after: string;
    cert_pem: string;
    chain_pem: string;
    privateKeyPem: string;
  } | null>(null);

  if (!token) {
    return (
      <section className="panel">
        <h3>Certificate enrollment</h3>
        <p className="admin-error">
          This page needs an enrollment link — ask the person who invited you
          for a fresh one.
        </p>
      </section>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { csrPem, privateKeyPem } = await generateKeyAndCsr(
        cn.trim(),
        email.trim() || undefined,
      );
      const resp = await fetch("/ca/enroll", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ csr_pem: csrPem }),
      });
      if (!resp.ok) {
        let detail = resp.statusText;
        try {
          detail = (await resp.json()).detail ?? detail;
        } catch {
          /* keep statusText */
        }
        throw new Error(detail);
      }
      const data = await resp.json();
      setResult({ ...data, privateKeyPem });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <h3>Certificate enrollment</h3>
      {result === null ? (
        <>
          <p className="muted">
            Your private key is generated <strong>in this browser</strong> and
            never sent anywhere. After enrolling, download the key and
            certificate — this page can't recover them later.
          </p>
          <form className="admin-form" onSubmit={submit}>
            <label>
              Name (or device id)
              <input
                value={cn}
                onChange={(e) => setCn(e.target.value)}
                autoFocus
                required
              />
              <span className="helper">
                This becomes the certificate's common name
              </span>
            </label>
            <label>
              Email (if this certificate is for email or login)
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <button type="submit" disabled={busy || !cn.trim()}>
              {busy ? "Enrolling…" : "Generate key & enroll"}
            </button>
          </form>
          {error && (
            <p className="admin-error" role="alert">
              {error}
            </p>
          )}
        </>
      ) : (
        <div className="issue-result" role="status">
          <p>
            Certificate issued for <strong>{result.subject}</strong>, valid to{" "}
            {result.not_after.slice(0, 10)}.
          </p>
          <p className="key-warning">
            ⚠ Download your private key now — it exists only on this page.
          </p>
          <div className="download-row">
            <button
              onClick={() => downloadText("key.pem", result.privateKeyPem)}
            >
              Download private key
            </button>
            <button
              className="ghost"
              onClick={() => downloadText("cert.pem", result.cert_pem)}
            >
              Certificate
            </button>
            <button
              className="ghost"
              onClick={() => downloadText("chain.pem", result.chain_pem)}
            >
              Chain
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
